import { environment } from '../../environments/environment';
import {
  buildReleaseFlags,
  InternalCrmDemoSalesReleaseFlags,
  ReleaseFeatureKey,
  ROUTE_RELEASE_FLAGS,
  MENU_LINK_RELEASE_FLAGS,
} from '../config/internal-crm-demo-sales-release.config';

export function getReleaseFlags(): InternalCrmDemoSalesReleaseFlags {
  const base = buildReleaseFlags(!!environment.production);
  const overrides =
    (environment as { internalCrmDemoSalesRelease?: Partial<InternalCrmDemoSalesReleaseFlags> })
      .internalCrmDemoSalesRelease ?? {};
  return { ...base, ...overrides };
}

export function isReleaseFeatureEnabled(flag: ReleaseFeatureKey): boolean {
  const flags = getReleaseFlags();
  return flags[flag] === true;
}

/** Test hub + parent nav visible when any test-platform capability is rolled out. */
export function isTestPlatformMenuEnabled(): boolean {
  const flags: ReleaseFeatureKey[] = [
    'testTenantsEnabled',
    'testProvisioningEnabled',
    'testSeedingEnabled',
  ];
  return flags.some((f) => isReleaseFeatureEnabled(f));
}

/** Demo hub + parent nav visible when any demo-platform capability is rolled out. */
export function isDemoPlatformMenuEnabled(): boolean {
  const flags: ReleaseFeatureKey[] = [
    'demoTenantsEnabled',
    'demoProvisioningEnabled',
    'demoPacksEnabled',
    'demoAnalyticsEnabled',
    'demoOperationsEnabled',
  ];
  return flags.some((f) => isReleaseFeatureEnabled(f));
}

/** Resolve required flag for a canonical admin route path. */
export function releaseFlagForRoute(url: string): ReleaseFeatureKey | null {
  const path = (url || '').split('?')[0].split('#')[0].replace(/^\/(en|fr|hi)(?=\/|$)/i, '');
  if (/\/organizations\/demo-workspaces\/[^/]+\/convert$/i.test(path)) {
    return 'demoConversionEnabled';
  }
  for (const { prefix, flag } of ROUTE_RELEASE_FLAGS) {
    if (path === prefix || path.startsWith(prefix + '/')) {
      return flag;
    }
  }
  return null;
}

export function isRouteReleaseEnabled(url: string): boolean {
  const flag = releaseFlagForRoute(url);
  if (!flag) {
    return true;
  }
  return isReleaseFeatureEnabled(flag);
}

export function isFeatureDisabledApiError(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const body = err as { error?: string; status?: number; statusCode?: number };
  if (body.error === 'FEATURE_DISABLED') {
    return true;
  }
  const status = body.status ?? body.statusCode;
  return status === 503 && body.error === 'FEATURE_DISABLED';
}

export function featureDisabledMessage(feature?: string): string {
  return feature
    ? `This feature (${feature}) is disabled in this environment. Contact platform ops to enable rollout flags.`
    : 'This feature is disabled in this environment. Contact platform ops to enable rollout flags.';
}
